import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  AdminMe, DashboardSummary, fetchAdminDashboard,
  RecentSignup, fetchRecentSignups,
} from '../lib/admin-portal-api';
import AdminPortalLayout from './AdminPortalLayout';
import {
  FaUserPlus, FaStar, FaClipboardList, FaDollarSign, FaBan, FaCalendarDay,
  IconType,
} from '../lib/icons';

interface Props { me: AdminMe }

export default function AdminPortalDashboard({ me }: Props) {
  const [data, setData] = useState<DashboardSummary | null>(null);
  const [recent, setRecent] = useState<RecentSignup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([fetchAdminDashboard(), fetchRecentSignups(8)])
      .then(([d, r]) => { setData(d); setRecent(r.data || []); })
      .catch(() => setError('Failed to load dashboard'))
      .finally(() => setLoading(false));
  }, []);

  const tiles: { label: string; value: number | string; IconC: IconType; tint: string; text: string }[] = data ? [
    { label: 'Users',            value: data.users.total,            IconC: FaUserPlus,       tint: 'from-blue-50 to-blue-100/40',    text: 'text-blue-700' },
    { label: 'Premium',          value: data.users.premium,          IconC: FaStar,           tint: 'from-purple-50 to-purple-100/40', text: 'text-purple-700' },
    { label: 'New (30d)',        value: data.users.new_30d,          IconC: FaCalendarDay,    tint: 'from-green-50 to-green-100/40',  text: 'text-green-700' },
    { label: 'Banned',           value: data.users.banned,           IconC: FaBan,            tint: 'from-red-50 to-red-100/40',      text: 'text-red-700' },
    { label: 'Pending posts',    value: data.community.pending_posts, IconC: FaClipboardList, tint: 'from-amber-50 to-amber-100/40',  text: 'text-amber-700' },
    { label: 'Influencers',      value: data.referrals.total,        IconC: FaDollarSign,     tint: 'from-orange-50 to-orange-100/40', text: 'text-orange-700' },
  ] : [];

  return (
    <AdminPortalLayout me={me} active="dashboard">
      <Helmet><title>Dashboard — EatMeat Admin</title></Helmet>

      <div className="mb-6">
        <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">Welcome back{me.firstname ? `, ${me.firstname}` : ''}.</h1>
        <p className="text-sm text-gray-500 mt-1">Quick view of the platform — click into any tile for detail.</p>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
          {Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-24 bg-white rounded-2xl animate-pulse" />)}
        </div>
      ) : error ? (
        <div className="px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">{error}</div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
          {tiles.map((t) => {
            const I = t.IconC;
            return (
              <div key={t.label} className={`relative overflow-hidden rounded-2xl px-4 py-4 bg-gradient-to-br ${t.tint} ring-1 ring-gray-100 shadow-sm`}>
                <div className="flex items-center justify-between">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">{t.label}</div>
                  <I className={`text-lg opacity-60 ${t.text}`} />
                </div>
                <div className={`text-3xl font-extrabold mt-2 ${t.text}`}>{t.value}</div>
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-8 grid md:grid-cols-3 gap-3">
        <Link to="/admin-portal/users" className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 hover:shadow-md transition-shadow">
          <div className="flex items-center gap-2 text-gray-800 font-semibold"><FaUserPlus className="text-blue-500" /> User lookup</div>
          <p className="text-xs text-gray-500 mt-1">Find a user by email or username, view subscription, ban or unban.</p>
        </Link>
        <Link to="/admin-portal/community" className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 hover:shadow-md transition-shadow">
          <div className="flex items-center gap-2 text-gray-800 font-semibold"><FaClipboardList className="text-amber-500" /> Community queue</div>
          <p className="text-xs text-gray-500 mt-1">Posts awaiting moderator review. Approve, hide, or delete.</p>
        </Link>
        {me.isSuperAdmin && (
          <Link to="/admin-portal/referrals" className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 hover:shadow-md transition-shadow">
            <div className="flex items-center gap-2 text-gray-800 font-semibold"><FaDollarSign className="text-orange-500" /> Referrals</div>
            <p className="text-xs text-gray-500 mt-1">Influencer accounts, commission earned, payout status.</p>
          </Link>
        )}
      </div>

      {/* Recent signups */}
      <div className="mt-8 bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-bold text-gray-800 inline-flex items-center gap-2"><FaUserPlus className="text-blue-500" /> Recent signups</h2>
          <Link to="/admin-portal/users" className="text-xs text-primary font-semibold hover:underline">All users →</Link>
        </div>
        {recent.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-6">No signups yet.</p>
        ) : (
          <div className="divide-y divide-gray-100">
            {recent.map((u) => (
              <Link key={u.id} to={`/admin-portal/users/${u.id}`} className="flex items-center justify-between gap-3 py-2.5 hover:bg-gray-50 px-2 rounded-lg">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-gray-800 truncate">{u.username || '—'}</div>
                  <div className="text-xs text-gray-500 truncate">{u.email}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[11px] text-gray-400">{new Date(u.createdAt).toLocaleDateString()}</div>
                  <div className="flex gap-1 mt-0.5 justify-end">
                    {u.is_premium && <span className="text-[10px] font-semibold text-purple-700 bg-purple-50 px-1.5 py-0.5 rounded">Premium</span>}
                    {!u.confirmed && <span className="text-[10px] font-semibold text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">Unverified</span>}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </AdminPortalLayout>
  );
}
